
import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { LanguageProvider } from "./i18n";
import { AuthProvider } from "./contexts/AuthContext";
import { SiteSettingsProvider } from "./hooks/useSiteSettings";
import WaitlistManagementPage from './pages/WaitlistManagementPage';

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import PushOptInBanner from "./components/PushOptInBanner";
import Louzinha from "./components/Louzinha";
import ProtectedRoute from "./components/ProtectedRoute";
import DynamicTheme from "./components/DynamicTheme";
import { InlineEditStyles } from "./components/InlineEdit";

// Pages
import HomePage from "./pages/HomePage";
import ServicesPage from "./pages/ServicesPage";
import AboutPage from "./pages/AboutPage";
import CulturePage from "./pages/CulturePage";
import MissionPage from "./pages/MissionPage";
import AuthCallback from "./pages/AuthCallback";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import ProfilePage from "./pages/ProfilePage";
import BookingPage from "./pages/BookingPage";
import PaymentReturnPage from "./pages/PaymentReturnPage";
import AdminPage from "./pages/AdminPage";
import SchedulePage from "./pages/SchedulePage";
import BeforeAfterPage from "./pages/BeforeAfterPage";
import StaffDashboardPage from "./pages/StaffDashboardPage";
import UserManagementPage from "./pages/UserManagementPage";
import NotificationsCenterPage from "./pages/NotificationsCenterPage";
import ResetPassword from "./pages/ResetPassword";
import PoliticaPrivacidade from "./pages/PoliticaPrivacidade";
import ExclusaoDados from "./pages/ExclusaoDados";
import TermosServico from "./pages/TermosServico";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Router wrapper to handle auth callback
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id (auth callback) SYNCHRONOUSLY
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <InlineEditStyles />
      <DynamicTheme />
      <Navbar />

      <main className="min-h-screen">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/culture" element={<CulturePage />} />
          <Route path="/mission" element={<MissionPage />} />
          <Route path="/before-after" element={<BeforeAfterPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/payment-return" element={<PaymentReturnPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route
            path="/politica-de-privacidade"
            element={<PoliticaPrivacidade />}
          />
          <Route path="/exclusao-de-dados" element={<ExclusaoDados />} />
          <Route path="/termos-de-servico" element={<TermosServico />} />
          <Route
  path="/gestao-lista-espera"
  element={
    <ProtectedRoute>
      <WaitlistManagementPage />
    </ProtectedRoute>
  }
/>

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/bookings"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/booking"
            element={
              <ProtectedRoute>
                <BookingPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/schedule"
            element={
              <ProtectedRoute>
                <SchedulePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/staff-dashboard"
            element={
              <ProtectedRoute>
                <StaffDashboardPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/user-management"
            element={
              <ProtectedRoute>
                <UserManagementPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/notificacoes"
            element={
              <ProtectedRoute>
                <NotificationsCenterPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>

      <Footer />
      <PushOptInBanner />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <SiteSettingsProvider>
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>

            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  background: "#1A1815",
                  color: "#fff",
                  border: "1px solid #BFA05F",
                },
              }}
            />
          </SiteSettingsProvider>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
